import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@codes/ui-kit/components/ui/tooltip';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { EllipsisVertical, MonitorSmartphone } from 'lucide-react';

import { usePrompts } from '@codes/agents';
import { chatbotsKeys, useBrains, useEmbeddings, useUpdateChat } from '@codes/chatbots';

import { ChatWindow } from './ChatWindow';

import type { ChatView } from '@lemoncloud/ssocio-chatbots-api';

interface ChatCardProps {
    chat: ChatView;
    index: number;
    isSending: boolean;
    onDelete: (chatId: string) => void;
    onScroll: (index: number, event: React.UIEvent<HTMLDivElement>) => void;
    scrollRef: (el: HTMLDivElement | null) => void;
    cardHeight: string;
}

export const ChatCard = ({ chat, index, isSending, onDelete, onScroll, scrollRef, cardHeight }: ChatCardProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { data: embeddingsData } = useEmbeddings({ limit: -1, stereo: 'system' });
    const { data: brainsData } = useBrains({ limit: -1 });
    const { data: promptsData } = usePrompts({ limit: -1 });

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        embeddingId: chat.embeddingId,
        brainId: chat.brainId,
        promptId: chat.promptId,
        name: chat.name || '',
    });

    const updateChat = useUpdateChat();

    const handleUpdateChat = async () => {
        try {
            await updateChat.mutateAsync({
                chatId: chat.id,
                name: editForm.name,
                embeddingId: editForm.embeddingId,
                brainId: editForm.brainId,
                promptId: editForm.promptId,
            });

            queryClient.setQueryData(chatbotsKeys.list({ rootId: chat.parentId, limit: 20 }), (oldData: any) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    data: oldData.data.map((item: ChatView) =>
                        item.id === chat.id
                            ? {
                                  ...item,
                                  name: editForm.name,
                                  embedding: editForm.embeddingId,
                                  chatType: editForm.brainId,
                                  prompt: editForm.promptId,
                              }
                            : item
                    ),
                };
            });

            setIsEditDialogOpen(false);
            toast({ title: 'Chat updated successfully' });
        } catch (error) {
            toast({ title: 'Failed to update chat' });
        }
    };

    const formatBrainName = (name: string | undefined) => {
        if (!name) {
            return '';
        }
        return name.replace('gpt-', 'g'); // 'g4' for gpt-4
    };

    const formatPromptName = (name: string | undefined) => {
        if (!name) {
            return '';
        }
        return name.replace('prompt', 'p'); // 'p3' for prompt3
    };

    const formatEmbeddingName = (name: string | undefined) => {
        if (!name) {
            return '';
        }
        const parts = name.split('-');
        return parts
            .slice(0, -1)
            .map(part => part[0])
            .concat(parts[parts.length - 1])
            .join('-');
    };

    return (
        <>
            <Card
                className={`max-w-[992px] min-w-[180px] flex flex-col py-6 px-[26px] bg-white dark:bg-[#1C1C1D] shadow-[0_0_10px_rgba(0,0,0,0.06)] border border-chatbot-line rounded-[16px] ${cardHeight}`}
            >
                <CardHeader className="p-0">
                    <CardTitle className="flex-1 text-sm font-medium flex items-center justify-between mb-2">
                        <div className="flex items-center">
                            <MonitorSmartphone className="h-5 w-5 mr-2" />
                            <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                    <TooltipTrigger className="hover:cursor-help">
                                        <span className="inline-flex items-center gap-1.5 max-w-[300px] truncate">
                                            {/* <span className="text-muted-foreground shrink-0">·</span> */}
                                            <span className="text-sub text-xs whitespace-nowrap">
                                                {formatPromptName(chat.prompt$?.name) || 'np'} ·{' '}
                                                {formatBrainName(chat.brain$?.name) || 'nb'} ·{' '}
                                                {formatEmbeddingName(chat.embedding$?.name)}
                                            </span>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-[300px]">
                                        <div className="space-y-1">
                                            <p className="text-xs text-yellow-400">
                                                <span className="font-medium text-foreground">Prompt:</span>{' '}
                                                {chat.prompt$?.name}
                                            </p>
                                            <p className="text-xs text-pink-400">
                                                <span className="font-medium text-foreground">Brain:</span>{' '}
                                                {chat.brain$?.name}
                                            </p>
                                            <p className="text-xs text-green-400">
                                                <span className="font-medium text-foreground">Embedding:</span>{' '}
                                                {chat.embedding$?.name}
                                            </p>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger className="group">
                                <EllipsisVertical className="w-4 h-4 text-sub group-hover:text-icon-hover" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="min-w-[108px] px-3 py-[6px] rounded-lg outline-none"
                            >
                                <DropdownMenuItem
                                    className="text-xs justify-center"
                                    onClick={() => setIsEditDialogOpen(true)}
                                >
                                    {t('ai.chatbot.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="my-[6px] bg-[#F4F5F5]"></DropdownMenuSeparator>
                                <DropdownMenuItem
                                    className="text-xs justify-center text-[#F34822] focus:text-[#F34822]"
                                    onClick={() => onDelete(chat.id)}
                                >
                                    {t('ai.chatbot.delete')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </CardTitle>
                </CardHeader>
                <CardContent
                    ref={scrollRef}
                    onScroll={e => onScroll(index, e)}
                    className="p-0 pt-[10px] text-sm overflow-y-auto h-full"
                >
                    <ChatWindow chat={chat} onScroll={onScroll} index={index} isSending={isSending} />
                </CardContent>
            </Card>

            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent>
                    <DialogTitle className="text-center">
                        {chat.name || `Chat #${chat.id}`} {t('ai.chatbot.edit')}
                    </DialogTitle>
                    <div className="px-6">
                        <div className="space-y-4 py-4">
                            <div>
                                <Label>{t('ai.chatbot.name')}</Label>
                                <Input
                                    value={editForm.name}
                                    onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder={t('ai.chatbot.name_placeholder')}
                                />
                            </div>
                            <div>
                                <Label>{t('ai.chatbot.embedding_model')}</Label>
                                <Select
                                    disabled
                                    value={editForm.embeddingId}
                                    onValueChange={value => setEditForm(prev => ({ ...prev, embeddingId: value }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('ai.chatbot.embedding_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {embeddingsData?.data.map(embedding => (
                                            <SelectItem key={embedding.id} value={embedding.id}>
                                                {embedding.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t('ai.chatbot.chat_type')}</Label>
                                <Select
                                    disabled
                                    value={editForm.brainId}
                                    onValueChange={value => setEditForm(prev => ({ ...prev, brainId: value }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('ai.chatbot.chat_type_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {brainsData?.data.map(brain => (
                                            <SelectItem key={brain.id} value={brain.id}>
                                                {brain.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>{t('ai.chatbot.prompt')}</Label>
                                <Select
                                    disabled
                                    value={editForm.promptId}
                                    onValueChange={value => setEditForm(prev => ({ ...prev, promptId: value }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('ai.chatbot.prompt_placeholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {promptsData?.data.map(prompt => (
                                            <SelectItem key={prompt.id} value={prompt.id}>
                                                {prompt.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 justify-center items-center mt-10">
                        <Button
                            variant="outline"
                            onClick={() => setIsEditDialogOpen(false)}
                            disabled={updateChat.isPending}
                            className="flex-1"
                        >
                            {t('ai.chatbot.cancel')}
                        </Button>
                        <Button onClick={handleUpdateChat} disabled={updateChat.isPending} className="flex-1">
                            {updateChat.isPending ? (
                                <div className="flex items-center gap-2">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                </div>
                            ) : (
                                t('ai.chatbot.edit')
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
